import React from 'react';
import { animated, OpaqueInterpolation, useSpring } from 'react-spring';

import { ContentCard, ContentCardIconType, VerticalSpacing, VerticalSpacingType } from 'ui/component';
import { SLIDE_SPEED_IN_MS, CARD_ANIMATION_SPEED_IN_MS } from '../../Constants';
import classNames from './EABDMachineLearningContentCard.module.scss';

export interface MachineLearningContentCardProps {
  readonly isInFocus: boolean;
  readonly style?: React.CSSProperties;
}

export const MachineLearningContentCard = React.memo<MachineLearningContentCardProps>((props) => {
  const { isInFocus, style } = props;

  const [isCountAnimationFinished, setCountAnimationFinished] = React.useState(false);
  const networkCoreImprovementProps = useSpring({ count: isCountAnimationFinished ? 25 : 0 });
  const reductionProps = useSpring({ count: isCountAnimationFinished ? 63 : 0 });
  const revenueProtectionProps = useSpring({ count: isCountAnimationFinished ? 5 : 0 });

  const NetworkCoreImprovementInterpolatorCallback = React.useCallback((countValue: number) => {
    const count = `${countValue.toFixed(0)}`;
    return `+${count}%`;
  }, []);

  const ReductionInterpolatorCallback = React.useCallback((countValue: number) => {
    const count = `${countValue.toFixed(0)}`;
    return `${count}%`;
  }, []);

  const RevenueProtectionInterpolatorCallback = React.useCallback((countValue: number) => {
    const count = `${countValue.toFixed(0)}`;
    return `up to ${count}%`;
  }, []);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;

    const handler = () => {
      setCountAnimationFinished(true);
    };

    if (isInFocus) {
      const timeoutInMs = SLIDE_SPEED_IN_MS + CARD_ANIMATION_SPEED_IN_MS;
      timeout = setTimeout(handler, timeoutInMs);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isInFocus]);

  const BodyWithBottomSpacing = React.useMemo(() => {
    return (
      <React.Fragment>
        <p className={classNames.body}>
          {'Applying AI technologies, such as machine learning, deep learning or NLP, to boost automation capabilities.'}
        </p>
        <VerticalSpacing type={VerticalSpacingType.X2} />
      </React.Fragment>
    );
  }, []);

  const RenderHeadlineCallback = React.useCallback((interpolation: OpaqueInterpolation<string>, caption: string) => {
    return (
      <div className={classNames.headlineListItem}>
        <animated.p className={classNames.headline}>{interpolation}</animated.p>
        <VerticalSpacing type={VerticalSpacingType.X05} />
        <p className={classNames.caption}>{caption}</p>
      </div>
    );
  }, []);

  return (
    <ContentCard title={'Machine Learning'} iconType={ContentCardIconType.CHIP} isInFocus={isInFocus} style={style}>
      {BodyWithBottomSpacing}
      <div className={classNames.headlineList}>
        {RenderHeadlineCallback(
          networkCoreImprovementProps.count.interpolate(NetworkCoreImprovementInterpolatorCallback),
          'network NPS™ score improvement'
        )}
        {RenderHeadlineCallback(
          reductionProps.count.interpolate(ReductionInterpolatorCallback),
          'reduction of priority 1 incidents in end-to-end automation'
        )}
        {RenderHeadlineCallback(
          revenueProtectionProps.count.interpolate(RevenueProtectionInterpolatorCallback),
          'revenue protection thanks to AI based optimization'
        )}
      </div>
    </ContentCard>
  );
});
